/**
 * The OAuth scopes, and what each one means to a person — V1-14.
 *
 * Here rather than in the API's domain because two places need them and
 * they must not drift: the API enforces the list, and the dashboard's
 * consent screen renders it. A scope added on one side and missing on
 * the other shows a person a bare `data:write` and asks them to approve
 * it, which is not consent so much as a shrug.
 *
 * A test in each package holds the two together.
 */
export const OAUTH_SCOPES = [
    'projects:read', 'projects:write',
    'data:read', 'data:write',
    'deploy',
    'logs:read',
    'billing:read',
];
export const isOAuthScope = (value) => OAUTH_SCOPES.includes(value);
/**
 * What approving each scope lets an application do, in the second
 * person, because that is who is reading it.
 *
 * Plain verbs over product nouns: a person manages "your projects", not
 * "the project resource". Every scope must have an entry - the type
 * requires it, and a test checks the rendered screen agrees.
 */
export const OAUTH_SCOPE_WORDS = {
    'projects:read': 'See your projects and their settings',
    'projects:write': 'Create, change and delete your projects, including issuing public application keys',
    'data:read': 'Read data in your projects',
    'data:write': 'Add, change and delete data in your projects',
    deploy: 'Deploy code to your projects',
    'logs:read': 'Read your logs',
    'billing:read': 'See your usage and billing',
};
/** Describe the effective project boundary without promising workspace authority. */
const PROJECT_SCOPE_WORDS = {
    'projects:read': 'See this project and its settings',
    'projects:write': 'Change and delete this project, including issuing public application keys',
    'data:read': 'Read data in this project',
    'data:write': 'Add, change and delete data in this project',
    deploy: 'Deploy code to this project',
    'logs:read': 'Read this project’s logs',
    'billing:read': 'This project-only authorization does not allow usage or billing access',
};
export function oauthScopeWords(scope, target = 'workspace') {
    if (!isOAuthScope(scope))
        return scope;
    return (target === 'project' ? PROJECT_SCOPE_WORDS : OAUTH_SCOPE_WORDS)[scope];
}
/** Project restriction cannot carry workspace provisioning/billing authority. */
export const PROJECT_GRANT_PERMISSIONS = [
    'org:read', 'project:read', 'project:update', 'project:delete',
    'data:read', 'data:write', 'deploy', 'logs:read',
];
export function projectGrantAllows(permission) {
    return PROJECT_GRANT_PERMISSIONS.includes(permission);
}
//# sourceMappingURL=oauth-scopes.js.map